import { PDFArray, PDFDict, PDFName, PDFNumber, PDFRawStream, PDFStream, decodePDFRawStream } from "pdf-lib";

/**
 * Detection of the printed rules under a multiline field, straight from the page
 * content.
 *
 * The XFA template is the authoritative source of a ruled field's line spacing
 * (see pdfRuledFields), but it does not survive every tool: the PDFs we store
 * are unlocked first, and iLovePDF drops /XFA entirely while keeping the
 * AcroForm and the page drawing intact. The rules themselves are still on the
 * page, so their spacing is the line height.
 *
 * This is a minimal content stream interpreter: it only tracks the CTM and the
 * path operators, which is all it takes to find horizontal strokes and hairline
 * fills. Text, images and colors are skipped.
 */

// A form XObject can nest others; this bounds a malicious or cyclic one.
const MAX_XOBJECT_DEPTH = 5;
// Shorter segments are tick marks or glyph-like ornaments, not writing lines.
const MIN_RULE_LENGTH = 10;
// A filled rectangle at most this tall is drawn as a line, not as a box.
const MAX_RULE_THICKNESS = 1.5;

const DELIMITERS = "()<>[]{}/%";
const isWhitespace = (c) => c === " " || c === "\n" || c === "\r" || c === "\t" || c === "\f" || c === "\0";
const isRegular = (c) => c !== undefined && !isWhitespace(c) && !DELIMITERS.includes(c);

const multiply = ([a, b, c, d, e, f], [A, B, C, D, E, F]) => [
    a * A + b * C,
    a * B + b * D,
    c * A + d * C,
    c * B + d * D,
    e * A + f * C + E,
    e * B + f * D + F,
];

const apply = ([a, b, c, d, e, f], x, y) => [a * x + c * y + e, b * x + d * y + f];

const decodeStream = (stream) =>
    stream instanceof PDFRawStream ? decodePDFRawStream(stream).decode() : stream.getContents();

const toLatin1 = (bytes) => {
    let out = "";
    // Chunked: String.fromCharCode(...bytes) overflows the stack on large pages.
    for (let idx = 0; idx < bytes.length; idx += 8192) {
        out += String.fromCharCode.apply(null, bytes.subarray(idx, idx + 8192));
    }
    return out;
};

const pageContentText = (page) => {
    const contents = page.node.Contents();
    const streams = contents instanceof PDFArray ? contents.asArray().map((ref) => page.doc.context.lookup(ref)) : [contents];
    return streams
        .filter((stream) => stream instanceof PDFStream)
        .map((stream) => toLatin1(decodeStream(stream)))
        .join("\n");
};

/** Index just past a literal string starting at `start` (the opening parenthesis). */
const skipLiteralString = (s, start) => {
    let depth = 0;
    for (let idx = start; idx < s.length; idx++) {
        const c = s[idx];
        if (c === "\\") idx++;
        else if (c === "(") depth++;
        else if (c === ")" && --depth === 0) return idx + 1;
    }
    return s.length;
};

/** Index just past a dictionary starting at `start` ("<<"), strings inside included. */
const skipDictionary = (s, start) => {
    let depth = 0;
    for (let idx = start; idx < s.length; idx++) {
        if (s[idx] === "(") idx = skipLiteralString(s, idx) - 1;
        else if (s[idx] === "<" && s[idx + 1] === "<") {
            depth++;
            idx++;
        } else if (s[idx] === ">" && s[idx + 1] === ">") {
            idx++;
            if (--depth === 0) return idx + 1;
        }
    }
    return s.length;
};

/**
 * Walks a content stream, calling `onOperator(operator, operands)` for every
 * operator. Operands are numbers, names ({ name }) or null for anything else.
 */
const tokenize = (s, onOperator) => {
    let operands = [];
    let idx = 0;

    while (idx < s.length) {
        const c = s[idx];

        if (isWhitespace(c)) {
            idx++;
        } else if (c === "%") {
            while (idx < s.length && s[idx] !== "\n" && s[idx] !== "\r") idx++;
        } else if (c === "(") {
            idx = skipLiteralString(s, idx);
            operands.push(null);
        } else if (c === "<" && s[idx + 1] === "<") {
            idx = skipDictionary(s, idx);
            operands.push(null);
        } else if (c === "<") {
            const end = s.indexOf(">", idx);
            idx = end < 0 ? s.length : end + 1;
            operands.push(null);
        } else if (c === "[" || c === "]" || c === "{" || c === "}" || c === ">") {
            idx++;
        } else if (c === "/") {
            let end = idx + 1;
            while (isRegular(s[end])) end++;
            operands.push({ name: s.slice(idx + 1, end) });
            idx = end;
        } else {
            let end = idx;
            while (isRegular(s[end])) end++;
            if (end === idx) end++; // a stray delimiter; never loop on it
            const word = s.slice(idx, end);
            idx = end;

            const number = Number(word);
            if (word !== "" && !Number.isNaN(number) && /^[+-]?(\d+\.?\d*|\.\d+)$/.test(word)) {
                operands.push(number);
                continue;
            }

            if (word === "BI") {
                // Inline image: its binary data could contain anything that looks like an operator.
                const data = s.slice(idx).search(/\sEI(\s|$)/);
                idx = data < 0 ? s.length : idx + data + 3;
            } else {
                onOperator(word, operands);
            }
            operands = [];
        }
    }
};

const numberAt = (dict, key, fallback) => {
    const value = dict.lookup(PDFName.of(key));
    return value instanceof PDFNumber ? value.asNumber() : fallback;
};

/**
 * Horizontal rules drawn by a content stream, in default user space:
 * [{ y, x1, x2 }] with x1 < x2.
 */
const collectRules = (doc, content, resources, initialCtm, depth, rules) => {
    let ctm = initialCtm;
    const ctmStack = [];
    let segments = []; // [[x1, y1, x2, y2]] in user space
    let thinRects = []; // [{ y, x1, x2 }]
    let current = null;
    let start = null;

    const stroke = () => {
        segments.forEach(([x1, y1, x2, y2]) => {
            if (Math.abs(y1 - y2) < 0.25 && Math.abs(x2 - x1) >= MIN_RULE_LENGTH) {
                rules.push({ y: (y1 + y2) / 2, x1: Math.min(x1, x2), x2: Math.max(x1, x2) });
            }
        });
    };
    const fill = () => rules.push(...thinRects);
    const endPath = () => {
        segments = [];
        thinRects = [];
        current = null;
        start = null;
    };

    tokenize(content, (op, args) => {
        switch (op) {
            case "q":
                ctmStack.push(ctm);
                break;
            case "Q":
                ctm = ctmStack.pop() ?? initialCtm;
                break;
            case "cm":
                if (args.length === 6 && args.every((arg) => typeof arg === "number")) ctm = multiply(args, ctm);
                break;
            case "m":
                current = apply(ctm, args[0], args[1]);
                start = current;
                break;
            case "l": {
                const next = apply(ctm, args[0], args[1]);
                if (current) segments.push([...current, ...next]);
                current = next;
                break;
            }
            case "h":
                if (current && start) segments.push([...current, ...start]);
                current = start;
                break;
            case "re": {
                const [x, y, w, h] = args;
                const corners = [apply(ctm, x, y), apply(ctm, x + w, y), apply(ctm, x + w, y + h), apply(ctm, x, y + h)];
                const xs = corners.map(([cx]) => cx);
                const ys = corners.map(([, cy]) => cy);
                const height = Math.max(...ys) - Math.min(...ys);
                const width = Math.max(...xs) - Math.min(...xs);
                // Axis-aligned hairline: the corners share their y two by two.
                if (height <= MAX_RULE_THICKNESS && width >= MIN_RULE_LENGTH) {
                    thinRects.push({ y: (Math.max(...ys) + Math.min(...ys)) / 2, x1: Math.min(...xs), x2: Math.max(...xs) });
                }
                current = corners[0];
                start = corners[0];
                break;
            }
            case "S":
            case "s":
                stroke();
                endPath();
                break;
            case "f":
            case "F":
            case "f*":
                fill();
                endPath();
                break;
            case "B":
            case "B*":
            case "b":
            case "b*":
                stroke();
                fill();
                endPath();
                break;
            case "n":
                endPath();
                break;
            case "Do": {
                if (depth >= MAX_XOBJECT_DEPTH || !args[0]?.name) break;
                const xObjects = resources?.lookup(PDFName.of("XObject"));
                const xObject = xObjects instanceof PDFDict ? xObjects.lookup(PDFName.of(args[0].name)) : null;
                if (!(xObject instanceof PDFStream)) break;
                if (xObject.dict.lookup(PDFName.of("Subtype"))?.asString?.() !== "/Form") break;

                const matrix = xObject.dict.lookup(PDFName.of("Matrix"));
                const formMatrix =
                    matrix instanceof PDFArray && matrix.size() === 6
                        ? matrix.asArray().map((value) => doc.context.lookup(value)?.asNumber?.() ?? 0)
                        : [1, 0, 0, 1, 0, 0];
                const formResources = xObject.dict.lookup(PDFName.of("Resources"));

                collectRules(
                    doc,
                    toLatin1(decodeStream(xObject)),
                    formResources instanceof PDFDict ? formResources : resources,
                    multiply(formMatrix, ctm),
                    depth + 1,
                    rules
                );
                break;
            }
            default:
                break;
        }
    });
};

/**
 * Every horizontal rule drawn on a page, in default user space - the same space
 * as the widgets' /Rect.
 */
export const collectPageRules = (page) => {
    const rules = [];
    try {
        const resources = page.node.Resources();
        collectRules(page.doc, pageContentText(page), resources, [1, 0, 0, 1, 0, 0], 0, rules);
    } catch (error) {
        // An unreadable page only costs us its rules, never the form.
        console.warn("Could not read the page drawing:", error.message);
    }
    return rules;
};

const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * The line layout implied by the rules printed under a field, in the same shape
 * as an XFA layout, or null when the field is not ruled.
 *
 * Conservative on purpose - a false positive would scatter the text of an
 * ordinary box - so it requires at least two evenly spaced rules spanning most
 * of the field, at a spacing that is plausible for its font size. Rules too close
 * to the field's top cannot have a line of text above them: that is the box's
 * own border.
 */
export const detectRuledLayout = (rules, rect, fontSize) => {
    const top = rect.y + rect.height;
    const ys = rules
        .filter((rule) => rule.y <= top - fontSize * 0.6 && rule.y >= rect.y - 1)
        .filter((rule) => Math.min(rule.x2, rect.x + rect.width) - Math.max(rule.x1, rect.x) >= rect.width * 0.7)
        .map((rule) => rule.y)
        .sort((a, b) => b - a)
        // The same rule is often drawn twice (stroke plus a hairline fill, or per page layer).
        .filter((y, idx, sorted) => idx === 0 || sorted[idx - 1] - y > 0.5);

    if (ys.length < 2) return null;

    const gaps = ys.slice(1).map((y, idx) => ys[idx] - y);
    const lineHeight = median(gaps);
    if (lineHeight < fontSize || lineHeight > fontSize * 3) return null;
    if (gaps.some((gap) => Math.abs(gap - lineHeight) > Math.max(0.5, lineHeight * 0.05))) return null;

    // Line k's box ends on rule k, so the first box starts one line height above
    // the first rule; and there are exactly as many lines as rules.
    const topInset = top - ys[0] - lineHeight;
    const bottomInset = rect.height - topInset - ys.length * lineHeight;

    return {
        lineHeight,
        // pdf-lib's own 1pt padding, so a ruled field lines up horizontally with the rest.
        marginLeft: 1,
        marginRight: 1,
        topInset,
        bottomInset,
        leftInset: 0,
        rightInset: 0,
    };
};
